import { Flex, Text } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { ReactNode } from 'react'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { isElectron } from '~/utils/common'

import { MessageQueueItem, ObjectValues } from '@mangovoice/types'

import { StorageKeys } from './StorageKeys'

interface ExperimentState {
  dashboardsEnabled: boolean
  callPopEnabled: boolean
}

const defaultExperimentState: ExperimentState = {
  dashboardsEnabled: false,
  callPopEnabled: false,
}

const experimentStateAtom = atomWithStorage<ExperimentState>(
  StorageKeys.Experiments,
  defaultExperimentState,
)

interface BasicExperiment {
  enabled: boolean
  title: string
  description: string
  internalOnly: boolean
  availableOn: AvailableOn
  onSet: (enabled: boolean) => void
  onLoad?: () => void
  children?: ReactNode
  badge?: {
    text: string
    styleVariant: NotificationBadgeVariant
  }
}

export const AVAILABLE_ON = {
  ALL: 'all',
  DESKTOP: 'desktop',
  WEB: 'web',
} as const

export type AvailableOn = ObjectValues<typeof AVAILABLE_ON>

const exampleTexts = [
  {
    id: 1569492,
    message_group_id: 1747,
    name: 'Mango User Two',
    external_phone_number: '+15555555555',
    unread_message_count: 3,
    latest_message: {
      id: 11994102,
      pbx_id: 4293,
      message_thread_id: 1569491,
      sender_user_id: undefined,
      direction: 'inbound',
      body: 'This is another test text message',
      files: [],
      created_at: '2025-02-23T23:04:04.000000Z',
      updated_at: '2025-02-23T23:04:04.000000Z',
    },
    created_at: '2025-02-17T17:31:13.000000Z',
    updated_at: '2025-02-23T23:04:04.000000Z',
    _id: '1569492',
    type: 'message',
  },
  {
    id: 1569491,
    message_group_id: 1747,
    name: 'Mango User',
    external_phone_number: '+15555555556',
    unread_message_count: 3,
    latest_message: {
      id: 11994101,
      pbx_id: 4293,
      message_thread_id: 1569491,
      sender_user_id: undefined,
      direction: 'inbound',
      body: 'This is a test text message',
      files: [],
      created_at: '2025-02-23T23:04:04.000000Z',
      updated_at: '2025-02-23T23:04:04.000000Z',
    },
    created_at: '2025-02-17T17:31:13.000000Z',
    updated_at: '2025-02-23T23:04:04.000000Z',
    _id: '1569491',
    type: 'message',
  },
]

export const useExperimentsState = () => {
  const [, setShowCallModal] = useSharedVariable<boolean>('ShowCallModal', true)
  const [, setMessageQueue] = useSharedVariable<MessageQueueItem[]>(
    'MessageQueue',
    [],
  )
  const [experimentState, setExperimentState] = useAtom(experimentStateAtom)
  const [experimentsRaw] = useLocalStorage<boolean>({
    key: 'experiments',
    initialValue: {},
  })

  const dashboardsExperiment: BasicExperiment = {
    enabled: experimentState.dashboardsEnabled ?? false,
    title: 'Dashboards',
    description: 'Enable the new dashboards feature.',
    onSet: (enabled: boolean) => {
      setExperimentState((prev) => ({
        ...prev,
        dashboardsEnabled: enabled,
      }))
    },
    internalOnly: true,
    availableOn: AVAILABLE_ON.ALL,
  }

  const callPopExperiment: BasicExperiment = {
    enabled: experimentState.callPopEnabled ?? true,
    title: 'Call and Text Notification Window',
    badge: {
      text: 'Beta',
      styleVariant: NotificationBadgeVariant.Beta,
    },
    internalOnly: false,
    availableOn: AVAILABLE_ON.DESKTOP,
    description:
      'Try out our new notification window feature. This feature will show a small, separate window when you receive a call or text message. This allows you to see call and text information without interrupting your work.',
    onSet: (enabled: boolean) => {
      setExperimentState((prev) => ({
        ...prev,
        callPopEnabled: enabled,
      }))

      if (isElectron) {
        window.electron?.popupWindow.sendExperimentSetting(
          JSON.stringify({ callPopEnabled: enabled }),
        )
        setShowCallModal(!enabled)
      }
    },
    onLoad: () => {
      if (isElectron) {
        // Since we're enabling this by default, we need to set the
        // localStorage value to true if it doesn't already exist.
        // We have to get the raw experiments value from localStorage,
        // because the useAtom will use a default value if it doesn't exist,
        // which would keep us from testing whether it already exists
        if (
          !Object.prototype.hasOwnProperty.call(
            experimentsRaw,
            'callPopEnabled',
          )
        ) {
          setExperimentState((prev) => ({
            ...prev,
            callPopEnabled: true,
          }))
        }
      }
    },
    children: (
      <Flex alignItems={'center'}>
        <MangoButton
          disabled={!experimentState.callPopEnabled}
          onClick={() => {
            setMessageQueue(exampleTexts as MessageQueueItem[])
          }}
        >
          Show example
        </MangoButton>
        <Text
          color={'mango.purple'}
          opacity={experimentState.callPopEnabled ? 0 : 1}
          marginLeft={2}
          fontSize="sm"
          aria-disabled={experimentState.callPopEnabled}
        >
          Toggle the switch above to enable the example button
        </Text>
      </Flex>
    ),
  }

  const experiments: BasicExperiment[] = [
    dashboardsExperiment,
    callPopExperiment,
  ]

  return {
    experiments,
    experimentState,
    actions: {},
  }
}
